/**
 * Customer types. Wire shapes (`Customer`, `CustomerSummary`, `HubspotCompanyMatch`,
 * etc.) are generated from the Rust DTOs in `src/transports/api/customers.rs`.
 *
 * The generated types have `lifecycle_state: string` and `crm_link_state: string`
 * because the Rust side serializes via `&'static str`; the SPA narrows them
 * here with intersections so pages see the typed unions below.
 */

import type { CustomerDto } from "./generated/CustomerDto";
import type { CustomerSummaryDto } from "./generated/CustomerSummaryDto";
import type { CustomerCrmStatusDto } from "./generated/CustomerCrmStatusDto";

import type { CustomerListResponse as CustomerListResponseDto } from "./generated/CustomerListResponse";
export type { HubspotCompanyMatchDto as HubspotCompanyMatch } from "./generated/HubspotCompanyMatchDto";
export type { HubspotSearchResponse } from "./generated/HubspotSearchResponse";
export type { IndustryDto as Industry } from "./generated/IndustryDto";
export type { CustomerDefaultUtilityDto as CustomerDefaultUtility } from "./generated/CustomerDefaultUtilityDto";

// ── Hand-written unions that narrow generated `string` fields ─────────────────

/**
 * CRM link state for both customers and contacts. Mirrors the Postgres
 * `crm_link_state` enum.
 *
 * - `unlinked`: no HubSpot record yet
 * - `linked`: live sync; edits automatically push
 * - `pending_create`: saved locally, HubSpot creation queued in outbox
 * - `pending_link`: linking to existing HubSpot record queued
 * - `pending_update`: edit saved, PATCH queued
 * - `sync_failed`: last HubSpot call errored; user can retry
 */
export type CrmLinkState =
  | "unlinked"
  | "linked"
  | "pending_create"
  | "pending_link"
  | "pending_update"
  | "sync_failed";

/** `true` when the state represents an in-flight HubSpot sync. */
export function isCrmSyncing(state: CrmLinkState): boolean {
  return (
    state === "pending_create" ||
    state === "pending_link" ||
    state === "pending_update"
  );
}

/** Terminal = not syncing. Polling can stop when this returns true. */
export function isCrmTerminal(state: CrmLinkState): boolean {
  return !isCrmSyncing(state);
}

/**
 * Default display label for each CRM link state. Matches the strings the
 * Rust side returns as `crm_link_state_label` so callers that *don't* pass
 * a server-provided label still get the same words. Kept in lock-step with
 * `app::customers::crm_link_state_label` on the server.
 */
export const CRM_LINK_STATE_LABELS: Record<CrmLinkState, string> = {
  unlinked: "Not Linked",
  linked: "Linked",
  pending_create: "Syncing",
  pending_link: "Syncing",
  pending_update: "Syncing",
  sync_failed: "Sync failed",
};

/**
 * Customer lifecycle state. Mirrors the Rust `CustomerStateDiscriminant`
 * enum on the wire (snake_case). Note that `Converted` is serialized as
 * `"customer"` to match the Postgres enum value.
 *
 * Keep this union in lock-step with `lifecycle_state_code` in
 * `src/transports/api/customers.rs`.
 */
export type CustomerLifecycleState =
  | "lead"
  | "mql"
  | "prospect"
  | "opportunity"
  | "customer"
  | "churned"
  | "closed_lost";

/** Human label paired with the code. Matches the server's `state_label`. */
export const LIFECYCLE_STATE_LABELS: Record<CustomerLifecycleState, string> = {
  lead: "Lead",
  mql: "MQL",
  prospect: "Prospect",
  opportunity: "Opportunity",
  customer: "Client",
  churned: "Churned",
  closed_lost: "Closed Lost",
};

// ── Narrowed re-exports of generated DTOs ─────────────────────────────────────

/**
 * Summary shape used in the list page. Narrows `CustomerSummaryDto` so the
 * SPA sees typed `lifecycle_state` / `crm_link_state` unions.
 *
 * The extra `"syncing" | "unknown"` tail on `crm_link_state` reflects a
 * pre-existing client-side convention for when the value hasn't arrived yet;
 * keep narrow to `CrmLinkState` proper once we're sure no call site relies
 * on those loose values.
 */
export type CustomerSummary = Omit<
  CustomerSummaryDto,
  "lifecycle_state" | "crm_link_state"
> & {
  lifecycle_state: CustomerLifecycleState;
  crm_link_state: CrmLinkState | "syncing" | "unknown";
};

/** Full customer detail — narrows `CustomerDto`'s string fields to typed unions. */
export type Customer = Omit<
  CustomerDto,
  | "lifecycle_state"
  | "crm_link_state"
  | "lifecycle_sync_state"
  | "lifecycle_synced_stage"
> & {
  lifecycle_state: CustomerLifecycleState;
  crm_link_state: CrmLinkState;
  /** Sync state of the lifecycle-stage push to HubSpot (own indicator). */
  lifecycle_sync_state: CrmLinkState;
  /** The lifecycle stage HubSpot last confirmed; null until first push. */
  lifecycle_synced_stage: CustomerLifecycleState | null;
};

/** CRM status poll response — narrows `CustomerCrmStatusDto`'s `crm_link_state`. */
export type CrmStatus = Omit<CustomerCrmStatusDto, "crm_link_state"> & {
  crm_link_state: CrmLinkState;
};

/** Paginated list response. Narrows the generated `items` array to the
 *  typed `CustomerSummary[]` above (the generated DTO has `string` fields
 *  where the SPA expects unions). */
export type CustomerListResponse = Omit<CustomerListResponseDto, "items"> & {
  items: CustomerSummary[];
};

// ── Mutation payloads (hand-written; Rust request structs not yet generated) ──

/** Aggregation type of a CCA community customer (AB#7087). Codes mirror the
 *  generated `AggregationType` union. */
export const AGGREGATION_TYPES = ["city", "township", "village"] as const;

export const AGGREGATION_TYPE_LABELS: Record<
  (typeof AGGREGATION_TYPES)[number],
  string
> = {
  city: "City",
  township: "Township",
  village: "Village",
};

/**
 * Payload for `POST /api/customers` (wizard step-3) and the re-link endpoint.
 * `crm_id` present = "link to existing"; absent/empty = "create new".
 */
export interface CustomerWizardPayload {
  name: string;
  crm_id?: string | null;
  industry?: string | null;
  website?: string | null;
  address1?: string | null;
  address2?: string | null;
  city?: string | null;
  state?: string | null;
  zip?: string | null;
  country?: string | null;
  /** Optional owning-agent user id. */
  agent_id?: string | null;
  /** The client's segments, as segment ids. Omitted leaves them unchanged on update. */
  segment_ids?: string[];
  /** Ohio county display string (AB#7087). */
  county?: string | null;
  /** Aggregation type code (AB#7087). */
  aggregation_type?: string | null;
  /**
   * Raw HubSpot `lifecyclestage` of the picked Company (echoed from the
   * search match). Seeds the new customer's lifecycle when linking to an
   * existing Company; omitted/ignored on the "create new" path.
   */
  lifecycle_stage?: string | null;
}

/** Payload for `PUT /api/customers/:id`. */
export interface UpdateCustomerPayload {
  name: string;
  industry?: string | null;
  website?: string | null;
  address1?: string | null;
  address2?: string | null;
  city?: string | null;
  state?: string | null;
  zip?: string | null;
  country?: string | null;
  agent_id?: string | null;
  /** The client's segments, as segment ids. Omitted leaves them unchanged on update. */
  segment_ids?: string[];
  /** Ohio county display string (AB#7087). */
  county?: string | null;
  /** Aggregation type code (AB#7087). */
  aggregation_type?: string | null;
}

/** Payload for `POST /api/customers/:id/relink`. */
export interface RelinkCustomerPayload extends CustomerWizardPayload {
  /** Empty string = create new; non-empty = link to this HubSpot Company. */
  crm_id: string;
}

/** Payload for `POST /api/customers/hubspot-search`. */
export interface HubspotSearchPayload {
  name: string;
  domain?: string | null;
}
